import { useEffect, useState } from 'react';

import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaCopy, FaDownload } from 'react-icons/fa6';
import { useApp } from 'common/AppContext';

const CREDENTIAL_OPTIONS = [
    { description: 'process.env.TEST_USERNAME / TEST_PASSWORD', label: 'Environment variables (recommended)', value: 'env_vars' },
    { description: 'Strings like "REPLACE_WITH_USERNAME" with a TODO', label: 'Placeholder strings', value: 'placeholders' },
    { description: 'Skip the login flow entirely — assume already authenticated', label: 'No credentials in test', value: 'skip' }
];

export const PlaywrightExportModal = ({ onHide, runUuid, show }) => {

    const { api } = useApp();

    const [testName, setTestName] = useState('');
    const [credentialStrategy, setCredentialStrategy] = useState('env_vars');
    const [includeScreenshots, setIncludeScreenshots] = useState(false);
    const [notes, setNotes] = useState('');

    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {

        if (!show) return;

        setTestName('');
        setCredentialStrategy('env_vars');
        setIncludeScreenshots(false);
        setNotes('');
        setResult(null);
        setError(null);
        setCopied(false);

    }, [show]);

    const generate = async () => {

        setGenerating(true);
        setError(null);
        setResult(null);

        try {

            const data = await api.post(`/api/runs/${runUuid}/playwright`, {
                credentialStrategy,
                includeScreenshots,
                notes: notes.trim() || null,
                testName: testName.trim() || null
            });
            setResult(data);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setGenerating(false);

        }

    };

    const copy = async () => {

        if (!result) return;
        try {

            await navigator.clipboard.writeText(result.content);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);

        } catch (_) { /* user can select+copy manually */ }

    };

    const download = () => {

        if (!result) return;
        const blob = new Blob([result.content], { type: 'text/typescript;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = result.filename || 'feature-tester-run.spec.ts';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);

    };

    return (
        <Modal centered scrollable onHide={onHide} show={show} size="lg">

            <Modal.Header closeButton>
                <Modal.Title>
                    Generate Playwright test
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                {error && (
                    <Alert dismissible onClose={() => setError(null)} variant="danger">
                        {error}
                    </Alert>
                )}

                {!result ? (
                    <>
                        <p className="text-muted small mb-3">
                            Capture this run as a TypeScript Playwright test you can replay in CI. The AI translates the
                            state-changing actions (navigate, click, type, asserts) and drops introspection (DOM snapshots, screenshots).
                        </p>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Test name
                                <span className="text-muted small ms-2">
                                    (leave blank to let the AI infer from the run)
                                </span>
                            </Form.Label>
                            <Form.Control
                                onChange={e => setTestName(e.target.value)}
                                placeholder="e.g. Student can switch the site language to Spanish"
                                value={testName}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Credentials
                            </Form.Label>
                            {CREDENTIAL_OPTIONS.map(o => (
                                <Form.Check
                                    label={(
                                        <>
                                            {o.label}
                                            <div className="text-muted small">
                                                {o.description}
                                            </div>
                                        </>
                                    )}
                                    checked={credentialStrategy === o.value}
                                    id={`cred-${o.value}`}
                                    key={o.value}
                                    name="credentialStrategy"
                                    onChange={() => setCredentialStrategy(o.value)}
                                    type="radio"
                                />
                            ))}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                checked={includeScreenshots}
                                id="include-screenshots"
                                label="Include screenshot captures (page.screenshot for each capture_evidence)"
                                onChange={e => setIncludeScreenshots(e.target.checked)}
                                type="checkbox"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Notes for the generator
                                <span className="text-muted small ms-2">
                                    (optional — anything specific to add or avoid)
                                </span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                onChange={e => setNotes(e.target.value)}
                                placeholder={'e.g. "use data-testid where possible"\n"add a final expect on the success toast text"'}
                                rows={3}
                                value={notes}
                            />
                        </Form.Group>
                    </>
                ) : (
                    <>
                        {result.notes && (
                            <Alert className="small" variant="info">
                                <strong>
                                    Notes from the generator:
                                </strong>
                                {' '}
                                {result.notes}
                            </Alert>
                        )}

                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <code className="small">
                                {result.filename}
                            </code>
                            <div className="d-flex gap-2">
                                <Button onClick={copy} size="sm" variant="outline-secondary">
                                    <FaCopy className="me-1" />
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                                <Button onClick={download} size="sm" variant="outline-secondary">
                                    <FaDownload className="me-1" />
                                    Download
                                </Button>
                            </div>
                        </div>

                        <Form.Control
                            readOnly
                            as="textarea"
                            onChange={() => {}}
                            rows={20}
                            style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                            value={result.content}
                        />
                    </>
                )}

            </Modal.Body>

            <Modal.Footer>
                <Button onClick={onHide} variant="outline-secondary">
                    Close
                </Button>
                {!result && (
                    <Button disabled={generating} onClick={generate} variant="primary">
                        {generating ? (
                            <>
                                <Spinner animation="border" as="span" className="me-2" size="sm" />
                                Generating…
                            </>
                        ) : 'Generate test'}
                    </Button>
                )}
                {result && (
                    <Button onClick={() => setResult(null)} variant="outline-primary">
                        Regenerate with different options
                    </Button>
                )}
            </Modal.Footer>

        </Modal>
    );

};
